import React, { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import {
  PageWrapper,
  Container,
  Header,
  HeaderTitle,
  HeaderSubtitle,
  CurrentPlanBadge,
  PeriodSelector,
  PeriodButton,
  SidebarOverlay,
  SidebarBackdrop,
  Sidebar,
  SidebarTitle,
  SidebarInfo,
  SidebarInfoRow,
  SidebarActions,
  SidebarActionBtn,
  PlanOptionsList,
  PlanOption,
  PlanOptionInfo,
  PlanOptionBtn,
  RenewalProgress,
  RenewalHeader,
  RenewalBar,
  RenewalFill,
  ViewPlansBtn,
  PlansSection,
  PlansGrid,
  PlanCard,
  PlanHeader,
  PlanName,
  BestValueBadge,
  PlanPricing,
  PlanPrice,
  PlanPeriodLabel,
  PlanSaving,
  ChoosePlanBtn,
  FeatureMatrixSection,
  FeatureMatrixCard,
  FeatureMatrixTitle,
  FeatureTable,
  FeatureTableHead,
  FeatureTableBody,
  FeatureCheck,
  FeatureX,
  FeatureText,
} from './Subscription.styled';

// Subscription Plans Config
const planGroups = {
  pharmacy: {
    label: 'Pharmacy',
    plans: [
      {
        id: 'pharmacy-basic',
        name: 'Basic',
        monthly: 59,
        features: {
          orders: true,
          invoicing: true,
          analytics: false,
          api: false,
          support: 'Email',
        },
      },
      {
        id: 'pharmacy-pro',
        name: 'Pro',
        monthly: 109,
        features: {
          orders: true,
          invoicing: true,
          analytics: true,
          api: false,
          support: 'Priority',
        },
      },
    ],
  },
  hospital: {
    label: 'Hospital',
    plans: [
      {
        id: 'hospital-enterprise',
        name: 'Enterprise',
        monthly: 249,
        features: {
          orders: true,
          invoicing: true,
          analytics: true,
          api: true,
          support: '24/7 Dedicated',
        },
      },
    ],
  },
  reseller: {
    label: 'Reseller',
    plans: [
      {
        id: 'reseller-starter',
        name: 'Starter',
        monthly: 39,
        features: {
          orders: true,
          invoicing: false,
          analytics: false,
          api: false,
          support: 'Email',
        },
      },
      {
        id: 'reseller-pro',
        name: 'Reseller Pro',
        monthly: 79,
        features: {
          orders: true,
          invoicing: true,
          analytics: true,
          api: true,
          support: 'Priority',
        },
      },
    ],
  },
};

const periods = [
  { id: 'monthly', label: 'Monthly', months: 1, discount: 0 },
  { id: 'quarterly', label: 'Quarterly', months: 3, discount: 10 },
  { id: 'semiannual', label: 'Semi-annual', months: 6, discount: 20 },
  { id: 'annual', label: 'Annual', months: 12, discount: 30 },
];

// Helpers
function priceWithDiscount(monthly, months, discount) {
  const full = monthly * months;
  const discounted = full * (1 - discount / 100);
  return {
    full,
    discounted,
    saving: full - discounted,
  };
}

export default function SubscriptionManager() {
  const [showSidebar, setShowSidebar] = useState(false);
  const [sidebarAction, setSidebarAction] = useState(null);

  // Subscription dates (mock)
  const subscriptionStart = new Date('2026-02-12T00:00:00Z');
  const renewalDate = new Date('2026-03-12T00:00:00Z');
  const totalPeriodMs = renewalDate.getTime() - subscriptionStart.getTime();

  const calcRenewal = () => {
    const now = Date.now();
    const remainingMs = Math.max(0, renewalDate.getTime() - now);
    const days = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
    const progress = totalPeriodMs > 0
      ? Math.min(100, Math.max(0, ((totalPeriodMs - remainingMs) / totalPeriodMs) * 100))
      : 0;
    return { days, progress };
  };

  const [{ days: remainingDays, progress: renewalProgress }, setRenewal] = useState(calcRenewal());

  useEffect(() => {
    const t = setInterval(() => setRenewal(calcRenewal()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const allPlans = Object.values(planGroups).flatMap((group) => group.plans);
  const [period, setPeriod] = useState(periods[0]);
  const [currentPlanId, setCurrentPlanId] = useState('pharmacy-pro');
  const currentPlan = allPlans.find((p) => p.id === currentPlanId);
  const isTrial = currentPlanId === 'trial';
  const isLowestPlan = currentPlan && !allPlans.some((p) => p.monthly < currentPlan.monthly);
  const [animateSavings, setAnimateSavings] = useState(false);

  const handlePeriodChange = (p) => {
    setPeriod(p);
    setAnimateSavings(true);
    setTimeout(() => setAnimateSavings(false), 600);
  };

  const handleBuy = (plan) => {
    setCurrentPlanId(plan.id);
  };

  const featureRows = [
    { key: 'orders', label: 'Order management' },
    { key: 'invoicing', label: 'Invoicing' },
    { key: 'analytics', label: 'Analytics & reports' },
    { key: 'api', label: 'API access' },
    { key: 'support', label: 'Support level' },
  ];

  return (
    <PageWrapper>
      <Container>
        {/* Header */}
        <Header>
          <div>
            <HeaderTitle>Subscription Plans</HeaderTitle>
            <HeaderSubtitle>
              Manage subscriptions for Pharmacies, Hospitals, and Resellers
            </HeaderSubtitle>
          </div>
          <CurrentPlanBadge
            $isPro={currentPlan?.name === 'Pro'}
            title={`You are currently on ${isTrial ? 'Trial' : currentPlan?.name || ''} plan`}
            onClick={() => setShowSidebar(true)}
          >
            {currentPlanId === 'trial'
              ? 'Trial plan'
              : `${allPlans.find((p) => p.id === currentPlanId)?.name || 'Account'} plan`}
          </CurrentPlanBadge>
        </Header>

        {/* Billing period selector */}
        <PeriodSelector>
          {periods.map((p) => (
            <PeriodButton
              key={p.id}
              $active={period.id === p.id}
              onClick={() => handlePeriodChange(p)}
            >
              {p.label}
            </PeriodButton>
          ))}
        </PeriodSelector>

        {/* Account sidebar */}
        {showSidebar && (
          <SidebarOverlay>
            <SidebarBackdrop onClick={() => setShowSidebar(false)} />
            <Sidebar>
              <SidebarTitle>Account plan</SidebarTitle>
              <SidebarInfo>
                <SidebarInfoRow>
                  <span>Current plan</span>
                  <span>{currentPlan?.name}</span>
                </SidebarInfoRow>
                <SidebarInfoRow>
                  <span>Subscribed on</span>
                  <span>12 Feb 2026</span>
                </SidebarInfoRow>
                <SidebarInfoRow>
                  <span className="text-highlight"> Next renewal</span>
                  <span className="text-highlight">12 Mar 2026</span>
                </SidebarInfoRow>
              </SidebarInfo>

              <SidebarActions>
                <SidebarActionBtn
                  $primary={currentPlan?.name === 'Pro'}
                  onClick={() => setSidebarAction('upgrade')}
                >
                  Upgrade plan
                </SidebarActionBtn>
                <SidebarActionBtn
                  disabled={isLowestPlan}
                  onClick={() => !isLowestPlan && setSidebarAction('downgrade')}
                >
                  Downgrade
                </SidebarActionBtn>
              </SidebarActions>

              {sidebarAction && (
                <PlanOptionsList>
                  {allPlans
                    .filter((p) =>
                      sidebarAction === 'upgrade'
                        ? currentPlan && p.monthly > currentPlan.monthly
                        : currentPlan && p.monthly < currentPlan.monthly
                    )
                    .map((p) => {
                      const pricing = priceWithDiscount(p.monthly, period.months, period.discount);
                      return (
                        <PlanOption key={p.id}>
                          <PlanOptionInfo>
                            <p>{p.name}</p>
                            <p>€{pricing.discounted.toFixed(0)} · {period.label}</p>
                          </PlanOptionInfo>
                          <PlanOptionBtn
                            onClick={() => {
                              setCurrentPlanId(p.id);
                              setSidebarAction(null);
                            }}
                          >
                            Confirm
                          </PlanOptionBtn>
                        </PlanOption>
                      );
                    })}
                </PlanOptionsList>
              )}

              {/* Renewal progress */}
              <RenewalProgress>
                <RenewalHeader>
                  <span>Renewal in</span>
                  <span>{remainingDays} days</span>
                </RenewalHeader>
                <RenewalBar>
                  <RenewalFill $progress={renewalProgress} />
                </RenewalBar>
              </RenewalProgress>

              <ViewPlansBtn
                onClick={() => {
                  setShowSidebar(false);
                  document.querySelector('#plans-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                View plans
              </ViewPlansBtn>
            </Sidebar>
          </SidebarOverlay>
        )}

        {/* Plans cards */}
        <PlansSection id="plans-section">
          {Object.values(planGroups).map((group) => (
            <div key={group.label} style={{ marginBottom: 48 }}>
              <PlansGrid>
                {group.plans.map((plan) => {
                  const pricing = priceWithDiscount(plan.monthly, period.months, period.discount);
                  return (
                    <PlanCard key={plan.id}>
                      <PlanHeader>
                        <PlanName>{plan.name}</PlanName>
                        {period.id === 'annual' && <BestValueBadge>Best value</BestValueBadge>}
                      </PlanHeader>

                      <PlanPricing>
                        <PlanPrice>€{pricing.discounted.toFixed(0)}</PlanPrice>
                        <PlanPeriodLabel>per {period.label.toLowerCase()}</PlanPeriodLabel>
                        {pricing.saving > 0 && (
                          <PlanSaving $animate={animateSavings}>
                            Save €{pricing.saving.toFixed(0)} vs monthly
                          </PlanSaving>
                        )}
                      </PlanPricing>

                      <ChoosePlanBtn onClick={() => handleBuy(plan)}>
                        Choose plan
                      </ChoosePlanBtn>
                    </PlanCard>
                  );
                })}
              </PlansGrid>
            </div>
          ))}
        </PlansSection>

        {/* Feature matrix */}
        <FeatureMatrixSection>
          {Object.values(planGroups).map((group) => (
            <FeatureMatrixCard key={group.label}>
              <FeatureMatrixTitle>{group.label} Feature Matrix</FeatureMatrixTitle>
              <div style={{ overflowX: 'auto' }}>
                <FeatureTable>
                  <FeatureTableHead>
                    <tr>
                      <th>Feature</th>
                      {group.plans.map((p) => (
                        <th key={p.id}>{p.name}</th>
                      ))}
                    </tr>
                  </FeatureTableHead>
                  <FeatureTableBody>
                    {featureRows.map((row) => (
                      <tr key={row.key}>
                        <td>{row.label}</td>
                        {group.plans.map((p) => (
                          <td key={p.id}>
                            {typeof p.features[row.key] === 'boolean' ? (
                              p.features[row.key] ? (
                                <FeatureCheck><Check size={16} /></FeatureCheck>
                              ) : (
                                <FeatureX><X size={16} /></FeatureX>
                              )
                            ) : (
                              <FeatureText>{String(p.features[row.key])}</FeatureText>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </FeatureTableBody>
                </FeatureTable>
              </div>
            </FeatureMatrixCard>
          ))}
        </FeatureMatrixSection>
      </Container>
    </PageWrapper>
  );
}
